export const BETA_ROUTE_PREFIX = "beta";
export const LEGACY_ROUTE_PREFIX = "legacy";

function readDefaultPortalExperience() {
    if (typeof document === "undefined") return "modern";
    const value = document
        .querySelector('meta[name="portal-default-experience"]')
        ?.getAttribute("content");
    return value === "legacy" ? "legacy" : "modern";
}

export const DEFAULT_PORTAL_EXPERIENCE = readDefaultPortalExperience();

function cleanRouteValue(page = "") {
    return String(page).replace(/^#\/?/, "").replace(/^\/+|\/+$/g, "");
}

export function isBetaRoute(page = "") {
    const value = cleanRouteValue(page);
    return value === BETA_ROUTE_PREFIX || value.startsWith(`${BETA_ROUTE_PREFIX}/`);
}

export function isLegacyRoute(page = "") {
    const value = cleanRouteValue(page);
    return value === LEGACY_ROUTE_PREFIX || value.startsWith(`${LEGACY_ROUTE_PREFIX}/`);
}

export function getBetaRoutePage(page = "") {
    const value = cleanRouteValue(page);
    if (!isBetaRoute(value)) return value || "dashboard";
    return value.slice(BETA_ROUTE_PREFIX.length).replace(/^\/+/, "") || "dashboard";
}

export function getLegacyRoutePage(page = "") {
    const value = cleanRouteValue(page);
    if (!isLegacyRoute(value)) return value || "dashboard";
    return value.slice(LEGACY_ROUTE_PREFIX.length).replace(/^\/+/, "") || "dashboard";
}

export function getPortalRoutePage(page = "") {
    if (isBetaRoute(page)) return getBetaRoutePage(page).split("?")[0] || "dashboard";
    if (isLegacyRoute(page)) return getLegacyRoutePage(page).split("?")[0] || "dashboard";
    return cleanRouteValue(page).split("?")[0] || "dashboard";
}

function getPortalRouteWithQuery(page = "") {
    const value = cleanRouteValue(page);
    if (isBetaRoute(value)) return getBetaRoutePage(value) || "dashboard";
    if (isLegacyRoute(value)) return getLegacyRoutePage(value) || "dashboard";
    return value || "dashboard";
}

export function getPortalExperience(page = "") {
    if (isBetaRoute(page)) return "modern";
    if (isLegacyRoute(page)) return "legacy";
    return DEFAULT_PORTAL_EXPERIENCE;
}

export function buildBetaHash(route = "dashboard") {
    const value = cleanRouteValue(route);
    const cleanRoute = value === BETA_ROUTE_PREFIX || value.startsWith(`${BETA_ROUTE_PREFIX}/`)
        ? value
        : `${BETA_ROUTE_PREFIX}/${value || "dashboard"}`;
    return `#/${cleanRoute}`;
}

export function buildLegacyHash(route = "dashboard") {
    const value = getPortalRouteWithQuery(route);
    return `#/${LEGACY_ROUTE_PREFIX}/${value || "dashboard"}`;
}

export function buildPortalHash(
    route = "dashboard",
    currentHash = typeof window !== "undefined" ? window.location.hash : ""
) {
    const value = getPortalRouteWithQuery(route);
    return getPortalExperience(currentHash) === "legacy"
        ? buildLegacyHash(value)
        : `#/${value || "dashboard"}`;
}

export function buildPortalPublicUrl(
    route,
    currentHash = typeof window !== "undefined" ? window.location.hash : "",
    origin = typeof window !== "undefined" ? window.location.origin : ""
) {
    const base = String(origin || "").replace(/\/+$/, "");
    const hash = buildPortalHash(route, currentHash);
    return base ? `${base}/${hash}` : hash;
}

export function getAuthenticatedLoginRedirectHash(
    hash = typeof window !== "undefined" ? window.location.hash : "",
    authenticated = false
) {
    if (!authenticated || getPortalRoutePage(hash) !== "login") return "";
    return buildPortalHash("dashboard", hash);
}

export function keepPortalExperienceInUrl(
    url,
    currentHash = typeof window !== "undefined" ? window.location.hash : ""
) {
    const value = String(url || "");
    if (!value) return value;

    const hashIndex = value.indexOf("#");
    if (hashIndex < 0) return value;

    const prefix = value.slice(0, hashIndex);
    const hash = value.slice(hashIndex);
    return `${prefix}${buildPortalHash(hash, currentHash)}`;
}

export function canonicalizePortalHash(hash = "") {
    const value = String(hash || "#/dashboard");
    if (!isBetaRoute(value) || DEFAULT_PORTAL_EXPERIENCE !== "modern") return value;
    // Recording beta is an explicit lab surface. Preserve its namespace so
    // the uploader can never be enabled accidentally by the default portal
    // experience or by a canonical redirect.
    const betaPage = getBetaRoutePage(value).split("?")[0];
    if (
        betaPage.startsWith("recording-studio/") ||
        betaPage.startsWith("recording-sessions/join/")
    ) return value;
    return `#/${getBetaRoutePage(value)}`;
}

export function keepBetaRouteInHash(hash = "", wasBeta = false) {
    const value = String(hash || "#/dashboard");
    const page = value.replace(/^#\/?/, "");
    if (!wasBeta || isBetaRoute(page)) {
        return value;
    }
    return buildBetaHash(page);
}
